import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { css } from 'twin.macro';
import { toast } from 'react-toastify';
import { WebSocketContext } from '@/_redux/actions/ActionSocket';
import { joinIvrCallAction, listenToIvrCallAction } from '@/_redux/actions/ActionCallCenter';
import { callCenterApi } from '@/_services/api/callCenter';
import { connect } from 'react-redux';
import ComponentErrorBoundary from '@/Layouts/ComponentErrorBoundary';
import Button from 'react-bootstrap/Button';

const bannerStyle = css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 8px 16px;
    background: linear-gradient(135deg, #1a73e8, #1557b0);
    color: #fff;
    font-size: 14px;

    .call-info {
        display: flex;
        align-items: center;
        gap: 8px;
        min-width: 0;

        .call-icon {
            font-size: 18px;
            animation: ringPulse 1.2s ease-in-out infinite;
        }

        .call-caller {
            font-weight: 600;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
        }

        .call-status {
            opacity: 0.85;
            font-size: 12px;
            white-space: nowrap;
        }

        .call-pin {
            background: rgba(255, 255, 255, 0.2);
            border: 1px solid rgba(255, 255, 255, 0.4);
            border-radius: 3px;
            padding: 2px 8px;
            font-family: monospace;
            font-size: 14px;
            font-weight: 700;
            letter-spacing: 2px;
            cursor: pointer;
            title: 'Click to copy PIN';
        }
    }

    .call-actions {
        display: flex;
        gap: 8px;
        flex-shrink: 0;
    }

    .invite-form {
        display: flex;
        align-items: center;
        gap: 6px;

        input,
        select {
            border-radius: 4px;
            border: 1px solid rgba(255, 255, 255, 0.5);
            background: rgba(255, 255, 255, 0.1);
            color: #fff;
            padding: 4px 8px;
            font-size: 13px;

            &::placeholder {
                color: rgba(255, 255, 255, 0.6);
            }

            option {
                color: #000;
            }
        }

        input {
            width: 160px;
        }
    }

    @keyframes ringPulse {
        0%,
        100% {
            transform: scale(1);
        }
        50% {
            transform: scale(1.2);
        }
    }
`;

function InboundCallBannerComponent({ room, joinIvrCallAction, listenToIvrCallAction }) {
    const ws = useContext(WebSocketContext);
    const [activeCall, setActiveCall] = useState(null);
    const [joinLoading, setJoinLoading] = useState(false);
    const [listenLoading, setListenLoading] = useState(false);
    const [joinedMembers, setJoinedMembers] = useState([]);
    const [showInvite, setShowInvite] = useState(false);
    const [invitePhone, setInvitePhone] = useState('');
    const [inviteMethod, setInviteMethod] = useState('sms');
    const [inviteLoading, setInviteLoading] = useState(false);
    const mountedRef = useRef(false);

    const roomId = room?.id || room?._id;
    const teamId = room?.team;

    const handleJoin = useCallback(async () => {
        if (!activeCall || !teamId) return;
        setJoinLoading(true);
        try {
            if (activeCall.status === 'claimed') {
                await joinIvrCallAction(teamId, activeCall.callId);
            } else {
                const result = await callCenterApi.claimIvrCall(teamId, activeCall.callId, { mode: 'join' });
                if (!mountedRef.current) return;
                setActiveCall((prev) =>
                    prev?.callId === activeCall.callId
                        ? { ...prev, status: 'claimed', accessPin: result?.accessPin || prev.accessPin }
                        : prev
                );
                toast.success('Your phone is being called to join.');
            }
        } catch (e) {
            if (mountedRef.current && activeCall.status !== 'claimed') {
                toast.error(e?.message || 'Could not join this call');
            }
        } finally {
            if (mountedRef.current) setJoinLoading(false);
        }
    }, [activeCall, teamId, joinIvrCallAction]);

    const handleListen = useCallback(async () => {
        if (!activeCall || !teamId) return;
        setListenLoading(true);
        try {
            if (activeCall.status === 'claimed') {
                await listenToIvrCallAction(teamId, activeCall.callId);
            } else {
                const result = await callCenterApi.claimIvrCall(teamId, activeCall.callId, { mode: 'listen' });
                if (!mountedRef.current) return;
                setActiveCall((prev) =>
                    prev?.callId === activeCall.callId
                        ? { ...prev, status: 'claimed', accessPin: result?.accessPin || prev.accessPin }
                        : prev
                );
                toast.success('Your phone is being called to listen.');
            }
        } catch (e) {
            if (mountedRef.current && activeCall.status !== 'claimed') {
                toast.error(e?.message || 'Could not listen to this call');
            }
        } finally {
            if (mountedRef.current) setListenLoading(false);
        }
    }, [activeCall, teamId, listenToIvrCallAction]);

    const handleInvite = useCallback(async () => {
        if (!activeCall || !teamId || !invitePhone.trim()) return;
        setInviteLoading(true);
        try {
            await callCenterApi.inviteToOngoingCall(teamId, activeCall.callId, {
                phoneNumber: invitePhone.trim(),
                method: inviteMethod,
            });
            if (!mountedRef.current) return;
            toast.success(`Invite sent via ${inviteMethod.toUpperCase()}`);
            setShowInvite(false);
            setInvitePhone('');
        } catch (e) {
            if (mountedRef.current) toast.error(e?.message || 'Failed to send invite');
        } finally {
            if (mountedRef.current) setInviteLoading(false);
        }
    }, [activeCall, teamId, invitePhone, inviteMethod]);

    const handleCopyPin = useCallback(() => {
        if (!activeCall?.accessPin) return;
        if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
            toast.info('Clipboard is unavailable. Copy the PIN manually.');
            return;
        }
        navigator.clipboard
            .writeText(activeCall.accessPin)
            .then(() => toast.success('PIN copied to clipboard'))
            .catch(() => toast.error('Could not copy the PIN. Copy it manually.'));
    }, [activeCall]);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        if (!ws?.socket || !roomId) return;

        const onIncomingCall = (data) => {
            if (mountedRef.current && data?.callId && data?.roomId && String(data.roomId) === String(roomId)) {
                setActiveCall({
                    callId: data.callId,
                    fromNumber: data.fromNumber,
                    status: 'ringing',
                    claimedBy: null,
                    accessPin: null,
                });
                setJoinedMembers([]);
                setShowInvite(false);
            }
        };

        const onCallClaimed = (data) => {
            if (mountedRef.current && data?.roomId && String(data.roomId) === String(roomId)) {
                setActiveCall((prev) => {
                    if (!prev || prev.callId !== String(data.callId)) return prev;
                    return {
                        ...prev,
                        status: 'claimed',
                        claimedBy: data.claimedByUserId,
                        accessPin: data.accessPin || prev.accessPin,
                    };
                });
            }
        };

        const onCallJoined = (data) => {
            if (mountedRef.current && data?.roomId && String(data.roomId) === String(roomId)) {
                setJoinedMembers((prev) => [
                    ...prev.filter((m) => m.userId !== data.joinedByUserId),
                    { userId: data.joinedByUserId, mode: data.mode },
                ]);
            }
        };

        const onCallMissed = (data) => {
            if (!mountedRef.current) return;
            setActiveCall((prev) => {
                if (!prev) return prev;
                if (data?.roomId && String(data.roomId) !== String(roomId)) return prev;
                if (data?.callId && prev.callId !== String(data.callId)) return prev;
                return null;
            });
        };

        ws.socket.on('incoming_call', onIncomingCall);
        ws.socket.on('call_claimed', onCallClaimed);
        ws.socket.on('call_joined', onCallJoined);
        ws.socket.on('call_missed', onCallMissed);

        return () => {
            ws.socket.off('incoming_call', onIncomingCall);
            ws.socket.off('call_claimed', onCallClaimed);
            ws.socket.off('call_joined', onCallJoined);
            ws.socket.off('call_missed', onCallMissed);
        };
    }, [ws, roomId]);

    useEffect(() => {
        if (!activeCall) {
            setJoinedMembers([]);
            setShowInvite(false);
        }
    }, [activeCall]);

    useEffect(() => {
        setActiveCall(null);
        setJoinedMembers([]);
        setShowInvite(false);
    }, [roomId]);

    useEffect(() => {
        const callId = activeCall?.callId;
        if (!callId) return undefined;

        let cancelled = false;
        let timeoutId;
        const endedStatuses = new Set(['busy', 'canceled', 'completed', 'failed', 'no_answer', 'ended', 'missed']);

        const checkCallStatus = async () => {
            try {
                const call = await callCenterApi.getCall(callId);
                if (cancelled || !mountedRef.current) return;
                const status = String(call?.callStatus || '')
                    .toLowerCase()
                    .replace(/-/g, '_');
                if (endedStatuses.has(status)) {
                    setActiveCall((current) => (current?.callId === callId ? null : current));
                    return;
                }
            } catch (error) {
                if (!cancelled && mountedRef.current && error?.code === 404) {
                    setActiveCall((current) => (current?.callId === callId ? null : current));
                    return;
                }
            }

            if (!cancelled && mountedRef.current) timeoutId = setTimeout(checkCallStatus, 10000);
        };

        checkCallStatus();
        return () => {
            cancelled = true;
            clearTimeout(timeoutId);
        };
    }, [activeCall?.callId]);

    if (!activeCall) return null;

    const statusText =
        activeCall.status === 'ringing'
            ? 'Incoming call — choose Join or Listen'
            : activeCall.status === 'claimed'
              ? `Call in progress${activeCall.claimedBy ? ' — claimed' : ''}`
              : 'Active call';

    const joinCount = joinedMembers.filter((m) => m.mode === 'join').length;
    const listenCount = joinedMembers.filter((m) => m.mode === 'listen').length;
    const isClaimed = activeCall.status === 'claimed';
    const canInvite = isClaimed && activeCall.accessPin;

    return (
        <div css={bannerStyle} data-testid="inbound-call-banner">
            <div className="call-info">
                <span className="call-icon">📞</span>
                <span className="call-caller">{activeCall.fromNumber || 'Unknown caller'}</span>
                <span className="call-status">
                    {statusText}
                    {joinCount > 0 && ` · ${joinCount} joined`}
                    {listenCount > 0 && ` · ${listenCount} listening`}
                </span>
                {activeCall.accessPin && (
                    <span
                        className="call-pin"
                        onClick={handleCopyPin}
                        title="Click to copy PIN"
                        data-testid="call-access-pin"
                    >
                        PIN: {activeCall.accessPin}
                    </span>
                )}
            </div>
            <div className="call-actions">
                {showInvite ? (
                    <div className="invite-form" data-testid="invite-form">
                        <input
                            type="tel"
                            placeholder="+15551234567"
                            value={invitePhone}
                            onChange={(e) => setInvitePhone(e.target.value)}
                            data-testid="invite-phone"
                        />
                        <select
                            value={inviteMethod}
                            onChange={(e) => setInviteMethod(e.target.value)}
                            data-testid="invite-method"
                        >
                            <option value="sms">SMS</option>
                            <option value="call">Call</option>
                        </select>
                        <Button
                            size="sm"
                            variant="light"
                            onClick={handleInvite}
                            disabled={inviteLoading || !invitePhone.trim()}
                            data-testid="btn-send-invite"
                        >
                            {inviteLoading ? 'Sending...' : 'Send'}
                        </Button>
                        <Button
                            size="sm"
                            variant="outline-light"
                            onClick={() => {
                                setShowInvite(false);
                                setInvitePhone('');
                            }}
                            data-testid="btn-cancel-invite"
                        >
                            Cancel
                        </Button>
                    </div>
                ) : (
                    <>
                        {canInvite && (
                            <Button
                                // size="sm"
                                variant="outline-light"
                                onClick={() => setShowInvite(true)}
                                disabled={joinLoading || listenLoading}
                                data-testid="btn-invite"
                            >
                                Invite
                            </Button>
                        )}
                        <Button
                            size="sm"
                            variant="warning"
                            onClick={handleListen}
                            disabled={listenLoading || joinLoading}
                            data-testid="btn-listen-ivr"
                        >
                            {listenLoading ? 'Connecting...' : 'Listen'}
                        </Button>
                        <Button
                            size="sm"
                            variant="success"
                            onClick={handleJoin}
                            disabled={joinLoading || listenLoading}
                            data-testid="btn-join-ivr"
                        >
                            {joinLoading ? 'Connecting...' : 'Join'}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
}

const ConnectedInboundCallBanner = connect(null, {
    joinIvrCallAction,
    listenToIvrCallAction,
})(InboundCallBannerComponent);

export default function InboundCallBanner(props) {
    return (
        <ComponentErrorBoundary componentErrorMessage="Call controls are unavailable. Refresh the channel to try again.">
            <ConnectedInboundCallBanner {...props} />
        </ComponentErrorBoundary>
    );
}
